/**
 * Engine and scene constants that do not change per campaign.
 *
 * Anything campaign-specific - model URL, size, animation, environment - lives
 * in `public/campaign.json` instead. See `src/content/manifest.types.ts`.
 */

export const CANVAS_ID = 'camerafeed'

export const MANIFEST_URL = '/campaign.json'

/**
 * Assumed eye height in metres. XR8's default 'responsive' scale mode treats
 * the camera's initial height above the origin as the ground distance, so this
 * value defines the world scale: with 1.6 here, 1 world unit == 1 metre.
 *
 * If placed models read consistently too large or small against the real room,
 * adjust this - the relationship is linear.
 */
export const CAMERA_HEIGHT_M = 1.6

/** Side length of the invisible ground plane used as the placement fallback. */
export const GROUND_SIZE_M = 60

/** Hits outside this range are implausible for a hand-held phone. */
export const MIN_PLACE_DISTANCE_M = 0.3
export const MAX_PLACE_DISTANCE_M = 8

/**
 * A lone bad feature point can drag a model through the floor. Reject feature
 * points this far from the running median of surface hits.
 */
export const GROUND_OUTLIER_TOLERANCE_M = 0.6

/** Sample size for the running ground-height median. */
export const GROUND_SAMPLE_COUNT = 30

export const SHADOW_OPACITY = 0.3
export const SHADOW_MAP_SIZE = 1024

/**
 * Half-extent of the directional light's orthographic shadow frustum.
 *
 * Sized for a roughly 1-2 m model. `setupLighting().followTarget()` keeps it
 * centred on the placed object, so it can be tight: at 1.5 m half-extent a
 * 1024 map gives ~2.9 mm texels, versus ~5.9 mm at 3 m. That difference is
 * visible on thin geometry like whiskers.
 */
export const SHADOW_FRUSTUM_M = 1.5

/**
 * Offset along the surface normal, in world metres, used to kill shadow acne.
 *
 * Keep it well under the thickness of the model's finest features - at 0.02
 * (2 cm) the sample is pushed clean off anything thinner than that and the
 * shadow visibly detaches (peter-panning).
 */
export const SHADOW_NORMAL_BIAS = 0.005
export const SHADOW_BIAS = -0.0005

/** Multiplier on the model's footprint radius when sizing the shadow catcher. */
export const SHADOW_CATCHER_SCALE = 3

/**
 * The entrance effect's beat sheet - "landing impact".
 *
 * It lives here and nowhere else. Everything downstream is expressed as a
 * fraction of the model's own footprint radius (`NormalizedModel.groundRadius`),
 * so a swapped-in GLB of any size gets the same silhouette over the same 0.8 s
 * with no retuning - the same reason `targetHeightMeters` exists instead of a
 * scale multiplier.
 *
 * Deliberately NOT derived from the intro clip's duration. Following the clip
 * would make the effect run at a different speed for every model, which is
 * exactly the swap-time surprise this file is meant to prevent. When they
 * disagree, `?debug=1` prints both side by side so it can be seen and the
 * numbers here adjusted once, on purpose.
 */
export const EFFECT_RING_SECONDS = 0.45
export const EFFECT_SPARK_DELAY_SECONDS = 0.05
export const EFFECT_SPARK_SECONDS = 0.75
export const EFFECT_SHADOW_FADE_SECONDS = 0.3
/** The last beat. The effect hides itself - and its draw calls - after it. */
export const EFFECT_TOTAL_SECONDS = EFFECT_SPARK_DELAY_SECONDS + EFFECT_SPARK_SECONDS

/**
 * The lead-in's beat sheet - "something is coming".
 *
 * A SECOND timeline, deliberately not folded into the EFFECT_* one above. The
 * landing effect fires when the character lands; this one runs BEFORE that, and
 * ends by cueing it. Merging them would mean the beat sheet owner also owned the
 * character's cue, and `src/effects/` is the one place documented as never
 * touching the model.
 *
 * Same bargain as EFFECT_*: the distances are fractions of the model's own
 * footprint radius so a swapped-in GLB gets the same gait untouched, while the
 * times are absolute so the beat sheet does not become a lie for anything but a
 * 1.2 m model.
 */
export const LEADIN_STEP_COUNT = 3
/** Gap between consecutive prints. This IS the walking cadence. */
export const LEADIN_STEP_INTERVAL_SECONDS = 0.16
/** How long one print takes to pop in. ~7 frames at 60 fps - enough to read. */
export const LEADIN_POP_SECONDS = 0.12
/** Held after the last print lands, before the character is cued. */
export const LEADIN_HOLD_SECONDS = 0.14

/**
 * The only number that says how long the user waits, and the one to look at
 * first when the lead-in feels wrong.
 *
 * Do NOT take this past ~0.6 s. On a hand-held phone the first hypothesis for
 * "I tapped and nothing came" is "it did not register", and the next action is
 * another tap - which hard-restarts the lead-in. Past the point where impatient
 * re-taps arrive faster than this elapses, the character genuinely never
 * appears, and the lack of a character is itself what drives the re-tapping.
 * The first print landing on the tap's own frame is the other half of that
 * defence; see `PlacementSequence.start`.
 */
export const LEADIN_TOTAL_SECONDS =
  (LEADIN_STEP_COUNT - 1) * LEADIN_STEP_INTERVAL_SECONDS +
  LEADIN_POP_SECONDS +
  LEADIN_HOLD_SECONDS

/**
 * How long the trail takes to fade once the character is out.
 *
 * Runs UNDER the landing effect's 0.8 s rather than before it. The overlap is
 * what makes the two halves read as one performance instead of two effects
 * played back to back.
 */
export const LEADIN_FADE_SECONDS = 0.35

/**
 * Stride and track width per step, as fractions of the footprint radius.
 *
 * CONSTRAINT: the whole trail must stay inside the shadow catcher, whose half
 * extent is `SHADOW_CATCHER_SCALE / 2` = 1.5 radii. The furthest print sits at
 * `LEADIN_STEP_COUNT * LEADIN_STRIDE_RATIO` = 1.26 along the approach, plus
 * 0.16 across and half an overshot print (0.22 * 1.105 / 2 = 0.12) of corner,
 * for about 1.39. Inside, but not by much - raising the stride or the step count
 * needs this recomputed.
 *
 * That bound is the whole reason the trail is short. `hit-test.ts` returns
 * POINTS and knows nothing about how far the surface extends - the geometric
 * fallback is a 60 m plane - and `facade.hitTest` only accepts screen
 * coordinates, so there is no way to ask "is there still floor a metre that
 * way": for a placement 5 m out, that point is often off-screen or behind the
 * camera. "The floor is flat for 1.5 radii around the placement" is a bet this
 * app already makes once, for the contact shadow. Staying inside it adds no
 * second bet. A trail that walks in from across the room needs a hit test that
 * reports plane EXTENTS, which is a different feature.
 */
export const LEADIN_STRIDE_RATIO = 0.42
export const LEADIN_LATERAL_RATIO = 0.16

/**
 * Bearing the trail arrives from, in degrees, measured in the placement root's
 * local frame from +Z (which `yawToFace` aims at the camera) toward +X (screen
 * right).
 *
 * 150 puts it behind the character and off to one side. Straight behind (180)
 * is the most legible "it walked up to here", but the character then stands
 * squarely in front of it and hides the trail for the whole fade, so the two
 * halves of the performance stop touching. Anything on the camera side (< 90)
 * reads backwards: prints between the viewer and the character say it walked
 * out of the lens.
 */
export const LEADIN_APPROACH_DEG = 150
/**
 * Toe-out per print, in degrees, applied with the sign of the foot.
 *
 * Quadrupeds splay. Three prints in a perfectly straight line with identical
 * headings read as a stamp being pressed by a machine, and this is the cheapest
 * thing that stops that - it is applied on the CPU while the corners are being
 * laid out, so it costs nothing at all at run time.
 */
export const LEADIN_SPLAY_DEG = 6

/**
 * Ring radius at `EFFECT_RING_SECONDS`, as a multiple of the footprint radius.
 *
 * Calibrated against a measurement, not a guess: CatMascot.glb at
 * targetHeightMeters 1.2 reports a groundRadius of 0.49 m - footprint radius is
 * half the WIDER of x/z, and the ears and arms make this model about as wide as
 * it is tall. 1.25 therefore lands the ring at ~0.61 m, just outside the feet
 * and comfortably inside the shadow catcher's 0.78 m half-extent.
 *
 * That 0.49 was 0.52 before the model revision that added `Bow`, which also
 * dropped the outermost whisker pair - the widest geometry on the model, so the
 * whole footprint moved with it. The later revision that baked the bow into
 * `Appear` changed animation data ONLY - mesh, skin, rest pose and materials are
 * byte-identical between the two files - so 0.49 carried over untouched
 * (re-measured at 0.4894).
 *
 * The revision that added `Spin` is the first one where "byte-identical" is NOT
 * the argument, so it is worth stating what replaced it. Mesh payloads,
 * materials and the node graph are still byte-identical, but `Foot.L/R` were
 * re-bound: their rest transforms changed (translation y -0.234 -> -0.364,
 * rotation dropped to identity) and the skin's inverse bind matrices were
 * recomputed to match. Those two cancel exactly - every joint's
 * `worldBind * IBM` is identity in both files, and the skinning matrices agree
 * to 2.5e-7 across all six shared clips - so `Box3.setFromObject` measures the
 * same bind pose and 0.4894 carries over again. Re-read `/debug`'s
 * `normalized.groundRadius` after any swap rather than trusting this line; the
 * number here is a record of what the scale was checked against, not an input to
 * anything.
 */
export const RING_RADIUS_SCALE = 1.25
/** Absolute bounds. A degenerate GLB must not yield an invisible or screen-filling ring. */
export const RING_MIN_RADIUS_M = 0.2
export const RING_MAX_RADIUS_M = 1.5
/** Inner edge of the ring geometry, as a fraction of its outer radius. */
export const RING_INNER_RATIO = 0.55

/**
 * The blink layer's cadence, in seconds of clip time.
 *
 * A range, not a value: a fixed interval reads as a machine inside about three
 * repetitions, and the irregularity is the entire effect. Absolute seconds and
 * deliberately NOT derived from the clip - how often a cat blinks is a property
 * of the cat, not of how long the artist made the squash take. (The clip's own
 * duration appears only in the double-blink gap below, where it must.)
 *
 * Here rather than in campaign.json for the same reason the EFFECT_* constants
 * are: this is the SHAPE of the behaviour, and a campaign choosing whether to
 * blink is a different job from a campaign authoring how.
 */
export const BLINK_INTERVAL_MIN_SECONDS = 3
export const BLINK_INTERVAL_MAX_SECONDS = 6

/**
 * Chance that a blink is immediately followed by a second one.
 *
 * Kept low. Much above this and the cat stops reading as calm and starts
 * reading as nervous, which is a different character.
 */
export const BLINK_DOUBLE_CHANCE = 0.25

/**
 * Eyes-open pause inside a double blink, in seconds.
 *
 * ADDED to the blink clip's own duration rather than used as the whole gap, so
 * the second blink can never cut the first one off. Restarting a one-shot
 * additive layer mid-flight snaps its delta to zero in a single frame, and on a
 * 0.65 eye-scale squash that is a visible flick. Expressing it as a pause also
 * means a swapped-in model with a slower blink keeps reading as a double blink
 * instead of degenerating into a stutter.
 */
export const BLINK_DOUBLE_GAP_SECONDS = 0.09

/**
 * Floor on the tap-target box's half-extents, as a fraction of the model's own
 * largest dimension.
 *
 * The volume is otherwise the model's measured bind-pose box, which is the
 * right answer for a mascot and the wrong one for a banner or a floor decal:
 * edge-on, that box is millimetres thick and cannot be hit with a finger.
 * Expressed against the model's own size for the same reason the EFFECT_*
 * constants are expressed against its footprint radius - a swapped-in GLB of
 * any scale gets the same feel with no retuning.
 *
 * It does not fire for the reference model (half-extents 0.484 / 0.594 / 0.371
 * in file units against a minimum of 0.143), so this is a safety net for
 * degenerate silhouettes, not a tuning knob.
 */
export const MODEL_HIT_MIN_EXTENT_RATIO = 0.12
/** Brightest radius, between RING_INNER_RATIO and 1. */
export const RING_PEAK_RATIO = 0.86
/** 48 keeps the chord error under 0.2 % - invisible - for 98 vertices. */
export const RING_SEGMENTS = 48
/**
 * Lift above the shadow catcher's y = 0.001 (scene/lighting.ts).
 *
 * ShadowMaterial does NOT disable depth writes, so the catcher writes depth and
 * the ring has to win the test on its own. 4 mm of clearance is roughly 10x the
 * depth resolution at 8 m on a 24-bit buffer.
 */
export const RING_LIFT_M = 0.005

export const SPARK_COUNT = 52
/** Fraction of the ring's max radius that sparks are emitted from. */
export const SPARK_SPREAD_RATIO = 0.8
/**
 * Initial velocities and gravity, in units of the spawn radius per second (and
 * per second squared). Expressing them this way is what makes the burst
 * self-similar across model sizes.
 */
export const SPARK_RISE_MIN = 1.7
export const SPARK_RISE_MAX = 2.7
export const SPARK_OUTWARD_MIN = 0.15
export const SPARK_OUTWARD_MAX = 0.7
export const SPARK_GRAVITY = -3
/**
 * Birth delay spread and lifetimes, in absolute seconds.
 *
 * Times alone are NOT scaled by model size: stretching them would make the beat
 * sheet above a lie for anything but a 1.2 m model.
 */
export const SPARK_STAGGER_SECONDS = 0.12
export const SPARK_LIFE_MIN_S = 0.45
export const SPARK_LIFE_MAX_S = 0.63
/** Point size in PointsMaterial's units, per spawn radius. */
export const SPARK_SIZE_MIN_RATIO = 0.1
export const SPARK_SIZE_MAX_RATIO = 0.22
/**
 * Hard ceiling on gl_PointSize.
 *
 * Apple GPUs commonly report an ALIASED_POINT_SIZE_RANGE upper bound of 64 and
 * clamp past it with no error, so a close-range placement would look different
 * on iOS than anywhere else. Clamping ourselves at 48 makes the look identical
 * everywhere AND bounds the burst's worst-case fill to 52 * 48^2 fragments.
 */
export const SPARK_MAX_POINT_PX = 48
/** Fixed seed so the Nth placement of a session always draws the same sparks. */
export const SPARK_SEED = 0x5eed

/**
 * How far the sparks are pulled toward white, in the linear working space
 * (0 = the same colour as the ring, 1 = pure white).
 *
 * The ring carries the model's hue; the sparks read as light. Without this, a
 * model whose hue is dark - a navy outline, say - drags the sparks down with it
 * and they stop registering as sparkle at all. Lerping toward white in LINEAR
 * space is the physically correct move here: it is literally adding white light,
 * which is what additive blending then does to the camera feed.
 */
export const SPARK_WHITEN = 0.55

/**
 * The paw print's look.
 *
 * The mask is drawn once into a canvas at run time rather than shipped as a
 * PNG or written as an SDF in the shader, and the reason is neither bytes nor
 * fill rate - it is who gets to change the shape. The ring and the sparks are
 * LIGHT, and their picture really is one line of `smoothstep`; a paw print is
 * ARTWORK, and the first campaign that swaps the cat for a dog needs its shape
 * changed by whoever drew the dog. `PAW_PATH_D` in src/effects/paw-mask.ts is
 * an SVG path, so that swap is a copy-paste out of any vector editor.
 *
 * Recorded because it will come up: a texture also filters correctly. A floor
 * decal is ALWAYS seen at a grazing angle, where mipmaps and anisotropy do the
 * right thing and a shader's `fwidth` antialiasing blows up along one axis and
 * smears. See src/effects/paw-mask.ts for the rest.
 */
export const PAW_MASK_PX = 256
/**
 * Anisotropic samples for the mask. Not the hardware maximum on purpose: extra
 * taps buy nothing on a silhouette this simple, and fill is the only cost that
 * matters on the target platform.
 */
export const PAW_ANISOTROPY = 8
/**
 * Blur radius of the dark halo, as a fraction of the mask's size.
 *
 * The halo is what makes the print legible on a floor whose brightness cannot
 * be known - see PAW_HALO_RATIO.
 */
export const PAW_MASK_HALO_BLUR_RATIO = 0.045
/** Side of one print's quad, as a fraction of the footprint radius. */
export const PAW_PRINT_SIZE_RATIO = 0.22
/**
 * Lift above the shadow catcher's y = 0.001 (scene/lighting.ts), below the
 * ring's y = RING_LIFT_M.
 *
 * ShadowMaterial does not disable depth writes, so the catcher writes depth and
 * this has to clear it. 2 mm is ~80 depth units at 2 m and still ~5 at the 8 m
 * placement limit. There is no z-fight against the ring at any distance,
 * because neither of them writes depth and `renderOrder` alone decides.
 */
export const PAW_LIFT_M = 0.003
/**
 * Peak alpha of the print body. High on purpose.
 *
 * NormalBlending, not additive, and this is the one place the paw trail
 * deliberately breaks ranks with the ring and the sparks. Additive disappears
 * on a bright floor and multiply disappears on a dark one, because both are
 * operations whose result DEPENDS on the camera feed's brightness - which, in
 * AR, is exactly the thing that cannot be known. Ordinary alpha REPLACES, so it
 * is the only one that looks the same on every floor. It also happens to be
 * what the picture means: the ring and the sparks are light; a print is matter
 * pressed into the ground.
 */
export const PAW_OPACITY = 0.82
/**
 * The dark halo's alpha, as a fraction of the body's.
 *
 * Pure black, so under premultiplied blending it contributes nothing to rgb and
 * only raises alpha - which composites as "coloured print over black halo over
 * camera feed" in a single pass. Same trick a UI drop shadow uses to keep an
 * icon readable over an unknown background, and here it is also physically
 * right: something pressed into a floor darkens its own edge.
 */
export const PAW_HALO_RATIO = 0.35
/**
 * How far the print's colour is pulled toward black in the LINEAR working
 * space.
 *
 * It is tempting to read this as the mirror of SPARK_WHITEN and push it up -
 * matter pressed into a floor should be dark. Do not. Measured against the
 * reference accent over floors from #f0efec down to #151413, the print is legible
 * across the whole range at 0 and at 0.15, weakens at 0.3, and at 0.5 it is
 * effectively gone on anything below about #2b2926.
 *
 * The asymmetry is what fixes the value: a too-BRIGHT print on a bright floor is
 * still separated by the halo, while a too-DARK print on a dark floor has
 * nothing left to separate it - that is the same trap that rules out multiply
 * blending in the first place. 0.15 keeps a hint of "pressed in" and stays well
 * inside the measured margin. Treat 0.3 as the ceiling.
 *
 * All of that was measured at PAW_OPACITY, i.e. alpha 0.82, and does NOT carry
 * over to anything drawn fainter. A pre-tap reticle once shipped at a third of
 * that alpha, passed every desktop check, and was reported as nothing at all on
 * a phone. Anything new drawn on the floor gets its own measurement.
 */
export const PAW_DARKEN = 0.15
/**
 * easeOutBack's overshoot constant, peaking at ~1.10x.
 *
 * Interpolated into the vertex shader the way RING_INNER_RATIO is, and NOT
 * mirrored into src/util/easing.ts: the curve is evaluated per print per frame
 * on the GPU, so a TypeScript copy would have no caller, and a second
 * implementation of the same curve is the exact hazard that file's header
 * warns about.
 */
export const PAW_POP_BACK = 1.70158

/**
 * Accent colour extraction - how the model's own colour becomes the effect's.
 *
 * Only the HUE is taken from the model; saturation and lightness are forced to
 * the constants below. That is not a shortcut, it is the whole trick. The
 * reference model's colours are a white body and a #004ea2 outline: averaging
 * them yields a muddy #b4bbd3, and the raw outline colour has a relative
 * luminance of 0.081, which under additive blending over a camera feed is
 * invisible. Pinning S and L means a pastel model and a deep crimson one glow
 * with the same strength, and only the hue tells them apart.
 */
export const ACCENT_SATURATION = 1
export const ACCENT_LIGHTNESS = 0.62
/**
 * Below this saturation a material counts as greyscale and contributes nothing.
 *
 * Saturation is also the weight, so this threshold is mostly belt-and-braces:
 * a white body scores zero on its own. That is what makes the inverted-hull
 * outline problem disappear without any special case - the toon outline shell
 * has MORE surface area than the body it wraps, so any area-weighted average
 * would be dragged toward it, but weighting by saturation ignores the white
 * entirely instead of fighting it.
 */
export const ACCENT_MIN_SATURATION = 0.15
/** Hue buckets, 30 degrees each. */
export const ACCENT_HUE_BINS = 12
/** Triangle budget for the area pass. Above it, sample with a stride. */
export const ACCENT_MAX_SAMPLED_TRIANGLES = 50_000
/** Base-colour textures are averaged after being drawn down to this square. */
export const ACCENT_TEXTURE_SAMPLE_PX = 16
/** Vertex-colour budget, sampled with a stride the same way. */
export const ACCENT_MAX_SAMPLED_VERTICES = 4096

/**
 * How long a placement sound stays valid while it waits for the AudioContext.
 *
 * The tap that places the model may not be able to unlock audio - `pointerdown`
 * does not reliably carry user activation on touch - and the matching
 * `pointerup` arrives tens of milliseconds later. This is the window in which
 * the queued sound is still a confirmation of that tap. Past it, playing would
 * just be noise over whatever happened next.
 */
export const AUDIO_PENDING_WINDOW_MS = 1000

/**
 * The same window for a tap-reaction sound, and much shorter on purpose.
 *
 * Normally a model tap can never find the context locked: placing the model
 * came first, and that gesture's `pointerup` already unlocked it. The case this
 * bridges is the return from an iOS interruption (a call, Siri) where the next
 * tap's `pointerdown` carries no activation and its `pointerup` unlocks tens of
 * milliseconds later. Within a quarter second the cue still lands inside the
 * 1.25-2.0 s clip it is keyed to and reads as that clip's voice; past it, the
 * sound stops being feedback for the tap. `arrive`'s full second would be wrong
 * here for the same reason it is right for a placement confirmation.
 */
export const AUDIO_REACT_PENDING_WINDOW_MS = 250

/**
 * iOS only. `'playback'` maps to AVAudioSession's MediaPlayback category in
 * WebKit's DOMAudioSession, which is the ONLY value that plays through the
 * hardware silent switch - `'ambient'`, `'transient'` and `'transient-solo'` all
 * map to AmbientSound / SoloAmbientSound and stay muted.
 *
 * Left at `'auto'` deliberately. MediaPlayback is an EXCLUSIVE category: it
 * stops whatever the user was already listening to, and taking someone's
 * podcast away for a half-second confirmation blip is a bad trade. It also
 * removes the platform's own mute control - the in-app mute UI (consent
 * dialog + toggle, see mute-state.ts) now exists, which was one precondition
 * for `'playback'`, but the exclusivity problem stands, so `'auto'` stays.
 *
 * UNVERIFIED ON DEVICE: the page holds a video-only getUserMedia stream while
 * this is set, and DOMAudioSession skips its own category bookkeeping once an
 * override exists. Do not flip without running the iOS checklist in
 * docs/モデル差し替え手順.md - in particular, that the camera feed does not stall.
 *
 * The explicit type annotation is load-bearing: without it TS infers the
 * literal type `'auto'` and every comparison against `'playback'` becomes an
 * "unintentional comparison" error.
 */
export const AUDIO_SESSION_TYPE: 'auto' | 'playback' = 'auto'

export const HEMI_LIGHT_INTENSITY = 1.2
export const DIR_LIGHT_INTENSITY = 2.0
export const ENVIRONMENT_INTENSITY = 0.6

/**
 * Longest edge of the saved photo, in pixels.
 *
 * The engine's `maxDimension` only ever scales DOWN, so a value at or above the
 * canvas's own size means "keep the camera's native resolution". Phones report
 * roughly 1280x720 to 1920x1440 here, so 1920 is effectively a pass-through and
 * the constant exists as a ceiling, not a target.
 */
export const CAPTURE_MAX_DIMENSION = 1920
/**
 * JPEG quality, 0..100.
 *
 * The engine defaults to 75, which is visibly blocky on the flat gradients a
 * camera feed is full of. 92 lands a 1920-wide frame at roughly 400-700 KB,
 * which the share sheet handles without a resize.
 */
export const CAPTURE_JPEG_QUALITY = 92
/** Filename stem. The timestamp is appended by `src/capture/photo.ts`. */
export const CAPTURE_FILE_PREFIX = 'webar'
/**
 * The shutter flash, in milliseconds.
 *
 * Deliberately shorter than a capture normally takes. It is feedback for the
 * tap, not a progress indicator - tying it to the promise would make a slow
 * capture look like a stuck screen.
 *
 * Note that the capture blocks the main thread for longer than this (see the
 * `afterNextPaint` comment in `src/ui/capture-ui.ts`), so on a slow phone the
 * flash is frozen at its peak for part of that time rather than fading on
 * schedule. That is the intended failure mode: a held whiteout reads as a
 * shutter, a missing one reads as a dead button.
 */
export const CAPTURE_FLASH_MS = 260
/**
 * Peak whiteness of the flash, 0..1.
 *
 * Not 1, and not the 0.9 this started at: at that strength the full-screen wash
 * swallows the shutter ring below it, and the brief the effect was built from
 * asked for a light flash rather than a blown-out frame. Expect to land
 * somewhere in 0.5..0.8 after looking at it outdoors, which is the only place
 * the answer is actually visible.
 */
export const CAPTURE_FLASH_PEAK_OPACITY = 0.62
/**
 * The ring that expands out of the shutter button, in milliseconds.
 *
 * Longer than the flash on purpose. The flash is a full-screen event and wants
 * to be over quickly; the ring is small, local to the button, and is what is
 * left to look at once the wash has gone.
 */
export const CAPTURE_RING_MS = 420
/**
 * How the three footsteps of the lead-in differ from each other, indexed by the
 * paw print's own index.
 *
 * `audio.step` is ONE file played three times, so without this it is audibly a
 * sample being retriggered rather than a creature walking. `gain` rising is what
 * makes it read as approaching; `rate` is the standard trick for hiding the
 * repeat - a few percent is inaudible as pitch and very audible as variety.
 *
 * The length of this table is what defines how many steps have a sound; a
 * lead-in with more prints than entries simply plays nothing for the extras
 * rather than reusing the last weight, which would be a silent lie about the
 * gait.
 */
export const ENTRANCE_STEP_WEIGHTS = [
  {gain: 0.55, rate: 1.0},
  {gain: 0.75, rate: 1.06},
  {gain: 1.0, rate: 0.97},
] as const

export const UI_TEXT = {
  initializing: 'スマホをゆっくり動かして、床を映してね',
  preparing: '準備中…',
  readyToPlace: '画面をタップして置いてね',
  // Shown for the length of the lead-in, and doing one job: telling the user the
  // tap DID register. Without it the only feedback during those 0.58 s is the
  // trail itself, and a user who does not yet know the trail means something
  // taps again - which restarts it. Atmospheric rather than instructional
  // because there is nothing to instruct; `refreshHint` early-returns once
  // placed, so nothing overwrites this until the character is out.
  arriving: 'あしあとが……なにか来るよ',
  // `#hint` is `white-space: nowrap`, so this line has a hard width budget:
  // at 15px it must fit inside a 375px viewport minus 40px of pill padding.
  placed: 'タップで移動・下のボタンで撮影',
  // Used instead of `placed` when the model actually has a reaction clip. It
  // spends the line on the affordance nothing on screen advertises: the shutter
  // is a 72px circle that reads as a shutter without being told, but "the
  // character answers back" is invisible until someone tries it.
  //
  // Model-agnostic on purpose - naming the cat here would put a per-campaign
  // string in the one file documented as not varying per campaign.
  placedReactive: 'タップで反応・床タップで移動',
  captureFailed: 'うまく撮れなかった…もう一度試してね',
  saveByLongPress: '画像を長押しすると保存できるよ',
} as const
